import { useModal } from "@racoon/ui";
import { useT, Namespace } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { useReissueJobMutation } from "~/shared/api/jobs/job.grpc";
import { JobPickerModalContent, JobPickerModalContentProps } from "./job-picker-modal";
import { JobPickerModalFooter } from "./job-picker-modal-footer";
import { createJobPickerSelection } from "./job-picker-selection";
import { reissueMissed } from "./reissue-missed";

export type OpenJobPickerOptions = Omit<JobPickerModalContentProps, "selection"> & {
  title?: string;
  confirmLabel?: string;
  onConfirm: (jobIds: number[], jobs: ConnectProto.Job[]) => void;
};

/**
 * Opens the reusable "select jobs" modal (unassigned and missed jobs,
 * multi-select) and resolves the chosen jobs through `onConfirm` — missed ones
 * already re-issued as new unassigned attempts — both their ids and the job
 * objects, since the picker's filters are its own and the caller's job query
 * may not cover what was picked. The modal closes itself on confirm. Reuse this
 * anywhere a job needs to be picked into a list.
 *
 * Header, body and footer are all ModalProvider slots; the selection shared by
 * body and footer lives in a per-open external store (see job-picker-selection).
 */
export function useJobPickerModal() {
  const { openModal, closeModal } = useModal();
  const t = useT();
  const [reissueJob] = useReissueJobMutation();

  const openJobPicker = ({ title, confirmLabel, onConfirm, ...rest }: OpenJobPickerOptions) => {
    const selection = createJobPickerSelection(rest.multiple ?? true);

    const id = openModal({
      size: "custom",
      width: { lg: "72svw" },
      height: { lg: "72svh" },
      className: "h-[85svh]",
      title: title ?? t(Namespace.Routes, "addJobsToRoute"),
      closeButtonTop: "top-3",
      headerClassName: "!p-4",
      bodyClassName: "!p-0 min-h-0 flex-1",
      body: <JobPickerModalContent {...rest} selection={selection} />,
      footer: (
        <JobPickerModalFooter
          selection={selection}
          confirmLabel={confirmLabel}
          onConfirm={async (_jobIds, jobs) => {
            const picked = await reissueMissed(jobs, (job) =>
              reissueJob({
                id: job.id,
                serviceProviderId: job.serviceProviderId,
                serviceReceiverId: job.serviceReceiverId,
              }).unwrap(),
            );
            // Nothing made it: keep the picker open rather than confirm an empty pick.
            if (picked.length === 0) return;
            onConfirm(
              picked.map((job) => Number(job.id)),
              picked,
            );
            closeModal(id);
          }}
        />
      ),
    });
    return id;
  };

  return { openJobPicker };
}
